import { fetchAPI } from "@coworker/app/src/hooks/API/tasks.service.helper";
import { useMyStore } from "@coworker/app/src/core/auth/useLoggedInUser";
import { useLanguage } from "@coworker/app/src/core/hooks/useLanguage";
import { pickSIKlocale } from "@coworker/utils/sikLocales";
import {
  RESULT_LIMIT_PER_PAGE,
  SIK_SEARCH,
  SIK_SEARCH_MORE,
  sikBaseUrl,
} from "@coworker/apprestructured/src/scan-and-search/constants/sikConstants";
import { SIK_API_VERSION, SIK_CLIENT_NAME } from "@coworker/consts/SIK";
import { useInfiniteQuery } from "@tanstack/react-query";
import {
  SikSearchMoreResponseType,
  SikSearchResponseType,
} from "@coworker/apprestructured/src/scan-and-search/types/SikSearchResponseType";

const fetchItemsFromSik = async (
  pageParam: { page: number; moreToken: string },
  primaryLocale: string,
  searchQuery: string
) => {
  const { page, moreToken } = pageParam;
  const endpoint = page === 1 ? SIK_SEARCH : SIK_SEARCH_MORE;
  const sikLocale = pickSIKlocale(primaryLocale);
  const url = new URL(`${sikBaseUrl}/${sikLocale}/${endpoint}`);

  url.searchParams.append("q", searchQuery);
  url.searchParams.append("v", SIK_API_VERSION);
  url.searchParams.append("c", SIK_CLIENT_NAME);
  url.searchParams.append("range", "ALL");
  url.searchParams.append("size", RESULT_LIMIT_PER_PAGE.toString());
  url.searchParams.append("types", "PRODUCT");

  if (moreToken) {
    url.searchParams.append("token", moreToken);
  }

  const { result } = await fetchAPI("GET", url);

  return result;
};
// lastPage: TQueryFnData, allPages: Array<TQueryFnData>, lastPageParam: TPageParam, allPageParams: Array<TPageParam>
const getNextPageParam = (
  lastPage: SikSearchResponseType | SikSearchMoreResponseType,
  _allPages: Array<SikSearchResponseType | SikSearchMoreResponseType>,
  lastPageParam: {
    page: number;
    moreToken: string;
  }
) => {
  if (
    lastPageParam.page === 1 &&
    (lastPage as SikSearchResponseType)?.searchResultPage.products.badge >
      parseInt(RESULT_LIMIT_PER_PAGE)
  ) {
    return {
      page: lastPageParam.page + 1,
      moreToken: (lastPage as SikSearchResponseType).searchResultPage.products
        .main.moreToken,
    };
  } else if (
    lastPageParam.page > 1 &&
    (lastPage as SikSearchMoreResponseType)?.more.max >
      (lastPage as SikSearchMoreResponseType)?.more.end
  ) {
    return {
      page: lastPageParam.page + 1,
      moreToken: (lastPage as SikSearchMoreResponseType).more.moreToken,
    };
  }
  return undefined;
};

export const useCallSikSearch = (searchQuery: string) => {
  const store = useMyStore();
  const language = useLanguage();
  const primaryLocale = store?.primaryLocale;
  const searchQueryLength = searchQuery.length * (language === "zh_CN" ? 3 : 1);

  return useInfiniteQuery({
    queryKey: ["sikSearch", primaryLocale, searchQuery],
    queryFn: ({ pageParam }) =>
      fetchItemsFromSik(pageParam, primaryLocale, searchQuery),
    staleTime: 0,
    initialPageParam: { page: 1, moreToken: "" },
    getNextPageParam: getNextPageParam,
    enabled: !!primaryLocale && searchQueryLength >= 2,
  });
};
